import axios from 'axios';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api`;

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

async function downloadPdf(url, filename) {
  const res = await axios.get(url, { headers: getHeaders(), responseType: 'blob' });
  const href = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

export const bookingApi = {
  // In-app messaging (/api/chat)
  chatUnreadCount: () => axios.get(`${API_URL}/chat/unread-count`, { headers: getHeaders() }),
  // kind 'LISTING': the Community shop chats, for the staff Messages page
  chatConversations: (archived = false, kind = null) => {
    const query = [archived && 'archived=1', kind && `kind=${kind}`].filter(Boolean).join('&');
    return axios.get(`${API_URL}/chat/conversations${query ? `?${query}` : ''}`, { headers: getHeaders() });
  },
  chatOpenInbox: () => axios.post(`${API_URL}/chat/inbox`, {}, { headers: getHeaders() }),
  chatOpenFamilyThread: (userId) => axios.post(`${API_URL}/chat/inbox/${userId}`, {}, { headers: getHeaders() }),
  chatMessages: (id, params = {}) =>
    axios.get(`${API_URL}/chat/conversations/${id}/messages`, { params, headers: getHeaders() }),
  chatSend: (id, formData) => axios.post(`${API_URL}/chat/conversations/${id}/messages`, formData, { headers: getHeaders() }),
  chatMarkRead: (id) => axios.post(`${API_URL}/chat/conversations/${id}/read`, {}, { headers: getHeaders() }),
  chatSetAlertsMuted: (id, muted) => axios.patch(`${API_URL}/chat/conversations/${id}/alerts`, { muted }, { headers: getHeaders() }),
  chatAttachment: (attachmentId) =>
    axios.get(`${API_URL}/chat/attachments/${attachmentId}`, { headers: getHeaders(), responseType: 'blob' }),
  chatEditMessage: (messageId, body, mentions) => axios.patch(`${API_URL}/chat/messages/${messageId}`, { body, mentions }, { headers: getHeaders() }),
  // The people the viewer may @mention in a conversation
  chatMentionable: (id) => axios.get(`${API_URL}/chat/conversations/${id}/mentionable`, { headers: getHeaders() }),
  // Adds the emoji, or takes it off if it's theirs already
  chatReact: (messageId, emoji) => axios.post(`${API_URL}/chat/messages/${messageId}/reactions`, { emoji }, { headers: getHeaders() }),
  chatDeleteMessage: (messageId) => axios.delete(`${API_URL}/chat/messages/${messageId}`, { headers: getHeaders() }),
  chatRemoveMessage: (messageId, reason) =>
    axios.post(`${API_URL}/chat/messages/${messageId}/remove`, { reason }, { headers: getHeaders() }),
  chatMessageHistory: (messageId) => axios.get(`${API_URL}/chat/messages/${messageId}/history`, { headers: getHeaders() }),
  chatReportMessage: (messageId, reason) =>
    axios.post(`${API_URL}/chat/messages/${messageId}/report`, { reason }, { headers: getHeaders() }),
  chatReports: () => axios.get(`${API_URL}/chat/reports`, { headers: getHeaders() }),
  chatResolveReport: (reportId) => axios.post(`${API_URL}/chat/reports/${reportId}/resolve`, {}, { headers: getHeaders() }),
  chatPeople: () => axios.get(`${API_URL}/chat/people`, { headers: getHeaders() }),
  chatGroupPreview: (data) => axios.post(`${API_URL}/chat/groups/preview`, data, { headers: getHeaders() }),
  chatGroup: (id) => axios.get(`${API_URL}/chat/groups/${id}`, { headers: getHeaders() }),
  chatCreateGroup: (data) => axios.post(`${API_URL}/chat/groups`, data, { headers: getHeaders() }),
  chatUpdateGroup: (id, data) => axios.patch(`${API_URL}/chat/groups/${id}`, data, { headers: getHeaders() }),
  chatAddGroupMember: (id, userId) => axios.post(`${API_URL}/chat/groups/${id}/members`, { userId }, { headers: getHeaders() }),
  chatRemoveGroupMember: (id, memberId) => axios.delete(`${API_URL}/chat/groups/${id}/members/${memberId}`, { headers: getHeaders() }),
  chatSetMemberPostingMuted: (id, memberId, postingMuted) =>
    axios.patch(`${API_URL}/chat/groups/${id}/members/${memberId}`, { postingMuted }, { headers: getHeaders() }),
  chatCompetitionGroup: (eventId) => axios.post(`${API_URL}/chat/competitions/${eventId}/group`, {}, { headers: getHeaders() }),

  // Community shop (/api/community)
  communityListings: (params = {}) => axios.get(`${API_URL}/community/listings`, { params, headers: getHeaders() }),
  communityMyListings: () => axios.get(`${API_URL}/community/listings/mine`, { headers: getHeaders() }),
  communityListing: (id) => axios.get(`${API_URL}/community/listings/${id}`, { headers: getHeaders() }),
  communityCreateListing: (formData) => axios.post(`${API_URL}/community/listings`, formData, { headers: getHeaders() }),
  communityUpdateListing: (id, formData) => axios.patch(`${API_URL}/community/listings/${id}`, formData, { headers: getHeaders() }),
  communitySetStatus: (id, status) => axios.post(`${API_URL}/community/listings/${id}/status`, { status }, { headers: getHeaders() }),
  communityRenew: (id) => axios.post(`${API_URL}/community/listings/${id}/renew`, {}, { headers: getHeaders() }),
  communityDeleteListing: (id) => axios.delete(`${API_URL}/community/listings/${id}`, { headers: getHeaders() }),
  communityInterested: (id) => axios.post(`${API_URL}/community/listings/${id}/interested`, {}, { headers: getHeaders() }),
  communityListingChats: (id) => axios.get(`${API_URL}/community/listings/${id}/chats`, { headers: getHeaders() }),
  communityReport: (id, reason) => axios.post(`${API_URL}/community/listings/${id}/report`, { reason }, { headers: getHeaders() }),
  communityRemove: (id, reason) => axios.post(`${API_URL}/community/listings/${id}/remove`, { reason }, { headers: getHeaders() }),
  communityPhoto: (photoId) => axios.get(`${API_URL}/community/photos/${photoId}`, { headers: getHeaders(), responseType: 'blob' }),
  communityTerms: () => axios.get(`${API_URL}/community/terms`, { headers: getHeaders() }),
  communityAcceptTerms: () => axios.post(`${API_URL}/community/terms`, {}, { headers: getHeaders() }),
  communityAdminListings: () => axios.get(`${API_URL}/community/admin/listings`, { headers: getHeaders() }),
  communityReportCount: () => axios.get(`${API_URL}/community/admin/reports/count`, { headers: getHeaders() }),

  getBookableGymnasts: () =>
    axios.get(`${API_URL}/gymnasts/bookable-for-me`, { headers: getHeaders() }),
  getAllClubGymnasts: () =>
    axios.get(`${API_URL}/gymnasts`, { headers: getHeaders() }),

  createSelfGymnast: (data) =>
    axios.post(`${API_URL}/gymnasts/self`, data, { headers: getHeaders() }),

  addChild: (data) =>
    axios.post(`${API_URL}/gymnasts/add-child`, data, { headers: getHeaders() }),

  updateGymnast: (gymnastId, data) =>
    axios.put(`${API_URL}/gymnasts/${gymnastId}`, data, { headers: getHeaders() }),

  updateEmergencyContact: (gymnastId, data) =>
    axios.patch(`${API_URL}/gymnasts/${gymnastId}/emergency-contact`, data, { headers: getHeaders() }),

  updateHealthNotes: (gymnastId, data) =>
    axios.patch(`${API_URL}/gymnasts/${gymnastId}/health-notes`, data, { headers: getHeaders() }),

  updateConsents: (gymnastId, data) =>
    axios.patch(`${API_URL}/gymnasts/${gymnastId}/consents`, data, { headers: getHeaders() }),

  setSessionTypeApproval: (gymnastId, sessionTypeId, approved) =>
    axios.put(`${API_URL}/gymnasts/${gymnastId}/session-type-approvals/${sessionTypeId}`, { approved }, { headers: getHeaders() }),

  setBgNumber: (gymnastId, bgNumber) =>
    axios.patch(`${API_URL}/gymnasts/${gymnastId}/bg-number`, { bgNumber }, { headers: getHeaders() }),
  verifyBgNumber: (gymnastId, action) =>
    axios.patch(`${API_URL}/gymnasts/${gymnastId}/bg-number/verify`, { action }, { headers: getHeaders() }),
  dismissBgNumber: (gymnastId, dismissed) =>
    axios.patch(`${API_URL}/gymnasts/${gymnastId}/bg-number/dismiss`, { dismissed }, { headers: getHeaders() }),
  archiveFromBg: (gymnastId) =>
    axios.patch(`${API_URL}/gymnasts/${gymnastId}/bg-number/archive`, {}, { headers: getHeaders() }),
  restoreGymnast: (gymnastId) =>
    axios.patch(`${API_URL}/gymnasts/${gymnastId}/restore`, {}, { headers: getHeaders() }),
  resubmitBgNumber: (gymnastId) =>
    axios.patch(`${API_URL}/gymnasts/${gymnastId}/bg-number/resubmit`, {}, { headers: getHeaders() }),
  getRequirementsQuestionStatus: () =>
    axios.get(`${API_URL}/requirements/questions/status`, { headers: getHeaders() }),
  askRequirementsQuestion: (question, history, context) =>
    axios.post(`${API_URL}/requirements/questions`, { question, history, context }, { headers: getHeaders() }),
  getAdminBgNumbers: () =>
    axios.get(`${API_URL}/gymnasts/admin/bg-numbers`, { headers: getHeaders() }),
  getBgLapseReports: () =>
    axios.get(`${API_URL}/bg-lapse-reports`, { headers: getHeaders() }),
  uploadBgLapseReport: (file, preview) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_URL}/bg-lapse-reports${preview ? '?preview=1' : ''}`, formData, { headers: getHeaders() });
  },
  checkBgLapseMailbox: () =>
    axios.post(`${API_URL}/bg-lapse-reports/check-mailbox`, {}, { headers: getHeaders() }),
  confirmBgLapse: (importId) =>
    axios.post(`${API_URL}/bg-lapse-reports/${importId}/confirm`, {}, { headers: getHeaders() }),
  dismissBgLapse: (importId) =>
    axios.post(`${API_URL}/bg-lapse-reports/${importId}/dismiss`, {}, { headers: getHeaders() }),
  undoBgLapse: (importId, gymnastId) =>
    axios.post(`${API_URL}/bg-lapse-reports/${importId}/undo`, { gymnastId }, { headers: getHeaders() }),
  resumeMembership: (membershipId) =>
    axios.post(`${API_URL}/booking/memberships/${membershipId}/resume`, {}, { headers: getHeaders() }),

  // Payment method (member-self)
  getMyPaymentMethod: () =>
    axios.get(`${API_URL}/booking/payment-method`, { headers: getHeaders() }),
  createPaymentMethodSetupIntent: () =>
    axios.post(`${API_URL}/booking/payment-method/setup-intent`, {}, { headers: getHeaders() }),
  confirmPaymentMethod: (paymentMethodId) =>
    axios.post(`${API_URL}/booking/payment-method/confirm`, { paymentMethodId }, { headers: getHeaders() }),
  // Back from a bank's page: the server reads what the SetupIntent saved
  confirmPaymentMethodSetup: (setupIntentId) =>
    axios.post(`${API_URL}/booking/payment-method/confirm`, { setupIntentId }, { headers: getHeaders() }),
  getMyInvoices: () =>
    axios.get(`${API_URL}/booking/payment-method/invoices`, { headers: getHeaders() }),

  // Payment method / invoices (admin read-only)
  getMemberPaymentMethod: (userId) =>
    axios.get(`${API_URL}/booking/admin/users/${userId}/payment-method`, { headers: getHeaders() }),
  getMemberInvoices: (userId) =>
    axios.get(`${API_URL}/booking/admin/users/${userId}/invoices`, { headers: getHeaders() }),
  getPayers: () =>
    axios.get(`${API_URL}/booking/admin/payers`, { headers: getHeaders() }),
  getMemberPayments: (userId) =>
    axios.get(`${API_URL}/booking/admin/users/${userId}/payments`, { headers: getHeaders() }),

  getSessions: (year, month) =>
    axios.get(`${API_URL}/booking/sessions`, {
      params: { year, month },
      headers: getHeaders(),
    }),

  getSession: (instanceId) =>
    axios.get(`${API_URL}/booking/sessions/${instanceId}`, { headers: getHeaders() }),

  cancelSession: (instanceId, reason) =>
    axios.patch(`${API_URL}/booking/sessions/${instanceId}/cancel`, { reason }, { headers: getHeaders() }),

  // Cover-coach register share links
  listRegisterTokens: (instanceId) =>
    axios.get(`${API_URL}/booking/admin/sessions/${instanceId}/register-tokens`, { headers: getHeaders() }),
  createRegisterToken: (instanceId, expiresInHours) =>
    axios.post(`${API_URL}/booking/admin/sessions/${instanceId}/register-token`, { expiresInHours }, { headers: getHeaders() }),
  revokeRegisterToken: (tokenId) =>
    axios.delete(`${API_URL}/booking/admin/sessions/register-tokens/${tokenId}`, { headers: getHeaders() }),

  createBooking: (data) =>
    axios.post(`${API_URL}/booking/bookings`, data, { headers: getHeaders() }),

  createBatchBooking: (data) =>
    axios.post(`${API_URL}/booking/bookings/batch`, data, { headers: getHeaders() }),

  // What a checkout would charge, as the server works it out (prices, credit,
  // the 30p card minimum); creates nothing
  quoteCheckout: (data) =>
    axios.post(`${API_URL}/booking/bookings/quote`, data, { headers: getHeaders() }),

  combinedCheckout: (data) =>
    axios.post(`${API_URL}/booking/bookings/combined`, data, { headers: getHeaders() }),

  getMyBookings: () =>
    axios.get(`${API_URL}/booking/bookings/my`, { headers: getHeaders() }),

  // The payment page of the family's own unpaid booking: { payable, clientSecret,
  // stripeAccountId, amount, creditApplied, total, lines } or { payable: false, message }
  getBookingCheckout: (bookingId) =>
    axios.get(`${API_URL}/booking/bookings/${bookingId}/checkout`, { headers: getHeaders() }),

  cancelBooking: (bookingId, options = {}) =>
    axios.post(`${API_URL}/booking/bookings/${bookingId}/cancel`, options, { headers: getHeaders() }),

  // Cancel a single gymnast off a multi-gymnast booking. Other lines stay confirmed.
  cancelBookingLine: (bookingId, lineId, options = {}) =>
    axios.post(`${API_URL}/booking/bookings/${bookingId}/lines/${lineId}/cancel`, options, { headers: getHeaders() }),

  getMyCredits: () =>
    axios.get(`${API_URL}/booking/credits/my`, { headers: getHeaders() }),

  applyCreditToMembership: (creditId) =>
    axios.post(`${API_URL}/booking/credits/${creditId}/apply-to-membership`, {}, { headers: getHeaders() }),

  getAllCredits: () =>
    axios.get(`${API_URL}/booking/credits/all`, { headers: getHeaders() }),

  assignCredit: (data) =>
    axios.post(`${API_URL}/booking/credits/assign`, data, { headers: getHeaders() }),

  // Names and slot labels for adding a membership (payments: Manage)
  getMembershipOptions: () =>
    axios.get(`${API_URL}/booking/memberships/options`, { headers: getHeaders() }),
  // Names and emails only, for picking who to charge or credit (payments or members access)
  getPayerChoices: () =>
    axios.get(`${API_URL}/users/payers`, { headers: getHeaders() }),
  getMembers: ({ includeArchived = false } = {}) =>
    axios.get(`${API_URL}/users${includeArchived ? '?includeArchived=true' : ''}`, { headers: getHeaders() }),

  archiveMember: (userId, reason) =>
    axios.patch(`${API_URL}/users/${userId}/archive`, { reason: reason || null }, { headers: getHeaders() }),

  restoreMember: (userId) =>
    axios.patch(`${API_URL}/users/${userId}/restore`, {}, { headers: getHeaders() }),

  getMember: (userId) =>
    axios.get(`${API_URL}/users/${userId}`, { headers: getHeaders() }),

  updateMemberProfile: (userId, data) =>
    axios.put(`${API_URL}/users/${userId}/profile`, data, { headers: getHeaders() }),

  createUser: (data) =>
    axios.post(`${API_URL}/users`, data, { headers: getHeaders() }),

  changeRole: (userId, role) =>
    axios.put(`${API_URL}/users/${userId}/role`, { role }, { headers: getHeaders() }),

  // Roles (Club admins only)
  getRoles: () =>
    axios.get(`${API_URL}/roles`, { headers: getHeaders() }),

  createRole: (data) =>
    axios.post(`${API_URL}/roles`, data, { headers: getHeaders() }),

  updateRole: (id, data) =>
    axios.put(`${API_URL}/roles/${id}`, data, { headers: getHeaders() }),

  deleteRole: (id) =>
    axios.delete(`${API_URL}/roles/${id}`, { headers: getHeaders() }),

  setUserRoles: (userId, roleIds) =>
    axios.put(`${API_URL}/users/${userId}/roles`, { roleIds }, { headers: getHeaders() }),

  resetPassword: (userId) =>
    axios.post(`${API_URL}/users/${userId}/reset-password`, {}, { headers: getHeaders() }),

  // Ends every sign in on your own account, this device's included
  signOutEverywhere: () =>
    axios.post(`${API_URL}/auth/sign-out-everywhere`, {}, { headers: getHeaders() }),

  adminAddToSession: (data) =>
    axios.post(`${API_URL}/booking/bookings/admin-add`, data, { headers: getHeaders() }),

  getClosures: () =>
    axios.get(`${API_URL}/booking/closures`, { headers: getHeaders() }),


  createClosure: (data) =>
    axios.post(`${API_URL}/booking/closures`, data, { headers: getHeaders() }),

  deleteClosure: (id) =>
    axios.delete(`${API_URL}/booking/closures/${id}`, { headers: getHeaders() }),

  getMemberships: () =>
    axios.get(`${API_URL}/booking/memberships`, { headers: getHeaders() }),

  createMembership: (data) =>
    axios.post(`${API_URL}/booking/memberships`, data, { headers: getHeaders() }),

  updateMembership: (id, data) =>
    axios.patch(`${API_URL}/booking/memberships/${id}`, data, { headers: getHeaders() }),

  deleteMembership: (id) =>
    axios.delete(`${API_URL}/booking/memberships/${id}`, { headers: getHeaders() }),
  resetMembership: (id, skipEmail = false) =>
    axios.post(`${API_URL}/booking/memberships/${id}/reset`, { skipEmail }, { headers: getHeaders() }),

  getMyMemberships: () =>
    axios.get(`${API_URL}/booking/memberships/my`, { headers: getHeaders() }),
  getMyPaymentIssues: () =>
    axios.get(`${API_URL}/booking/memberships/my/payment-issues`, { headers: getHeaders() }),

  getMembershipClientSecret: (membershipId) =>
    axios.get(`${API_URL}/booking/memberships/${membershipId}/client-secret`, { headers: getHeaders() }),

  getMembershipSetupIntent: (membershipId) =>
    axios.post(`${API_URL}/booking/memberships/${membershipId}/setup-intent`, {}, { headers: getHeaders() }),

  confirmMembershipPaymentMethod: (membershipId, paymentMethodId) =>
    axios.post(`${API_URL}/booking/memberships/${membershipId}/confirm-payment-method`, { paymentMethodId }, { headers: getHeaders() }),
  confirmMembershipPaymentMethodSetup: (membershipId, setupIntentId) =>
    axios.post(`${API_URL}/booking/memberships/${membershipId}/confirm-payment-method`, { setupIntentId }, { headers: getHeaders() }),

  payMembershipOpenInvoice: (membershipId) =>
    axios.post(`${API_URL}/booking/memberships/${membershipId}/pay-open-invoice`, {}, { headers: getHeaders() }),

  startMembershipDirectDebitSetup: (membershipId) =>
    axios.post(`${API_URL}/booking/memberships/${membershipId}/direct-debit-setup`, {}, { headers: getHeaders() }),

  getMembershipDirectDebitSetup: (membershipId, sessionId) =>
    axios.get(`${API_URL}/booking/memberships/${membershipId}/direct-debit-setup/${encodeURIComponent(sessionId)}`, { headers: getHeaders() }),

  // Switch a Direct Debit membership to the card just saved (or, back from a
  // bank's page, the card its SetupIntent saved)
  switchMembershipToCard: (membershipId, paymentMethodId) =>
    axios.post(`${API_URL}/booking/memberships/${membershipId}/switch-to-card`, { paymentMethodId }, { headers: getHeaders() }),
  switchMembershipToCardSetup: (membershipId, setupIntentId) =>
    axios.post(`${API_URL}/booking/memberships/${membershipId}/switch-to-card`, { setupIntentId }, { headers: getHeaders() }),

  payMembershipFirstMonth: (membershipId) =>
    axios.post(`${API_URL}/booking/memberships/${membershipId}/first-month-payment`, {}, { headers: getHeaders() }),

  getDelinquentMemberships: () =>
    axios.get(`${API_URL}/booking/memberships/delinquent`, { headers: getHeaders() }),
  acknowledgePaymentIssue: (membershipId, { issue, amount }) =>
    axios.post(`${API_URL}/booking/memberships/delinquent/${membershipId}/acknowledge`, { issue, amount }, { headers: getHeaders() }),
  unacknowledgePaymentIssue: (membershipId) =>
    axios.delete(`${API_URL}/booking/memberships/delinquent/${membershipId}/acknowledge`, { headers: getHeaders() }),
  checkMembershipPayments: () =>
    axios.post(`${API_URL}/booking/memberships/check-payments`, {}, { headers: getHeaders() }),

  sendMembershipReminder: (id) =>
    axios.post(`${API_URL}/booking/memberships/${id}/remind`, {}, { headers: getHeaders() }),

  notifyScheduledMemberships: () =>
    axios.post(`${API_URL}/booking/memberships/notify-scheduled`, {}, { headers: getHeaders() }),

  joinWaitlist: (instanceId) =>
    axios.post(`${API_URL}/booking/waitlist/${instanceId}`, {}, { headers: getHeaders() }),

  leaveWaitlist: (instanceId) =>
    axios.delete(`${API_URL}/booking/waitlist/${instanceId}`, { headers: getHeaders() }),

  getMyWaitlist: () =>
    axios.get(`${API_URL}/booking/waitlist/my`, { headers: getHeaders() }),

  adminAddChild: (data) =>
    axios.post(`${API_URL}/gymnasts/admin-add-child`, data, { headers: getHeaders() }),

  markAdultAsGymnast: (userId, extra = {}) =>
    axios.post(`${API_URL}/gymnasts/admin-mark-adult`, { userId, ...extra }, { headers: getHeaders() }),

  unlinkGymnastUser: (id, body = {}) =>
    axios.patch(`${API_URL}/gymnasts/${id}/unlink-user`, body, { headers: getHeaders() }),

  deleteGymnast: (id) =>
    axios.delete(`${API_URL}/booking/admin/gymnasts/${id}`, { headers: getHeaders() }),

  deleteMember: (userId) =>
    axios.delete(`${API_URL}/booking/admin/members/${userId}`, { headers: getHeaders() }),

  testEmail: (email) =>
    axios.post(`${API_URL}/users/test-email`, { email }, { headers: getHeaders() }),

  // Messages
  getMessages: () =>
    axios.get(`${API_URL}/messages`, { headers: getHeaders() }),
  getMessage: (id) =>
    axios.get(`${API_URL}/messages/${id}`, { headers: getHeaders() }),
  previewRecipients: (recipientFilter) =>
    axios.post(`${API_URL}/messages/preview-recipients`, { recipientFilter }, { headers: getHeaders() }),
  createMessage: (data) =>
    axios.post(`${API_URL}/messages`, data, { headers: getHeaders() }),
  updateMessage: (id, data) =>
    axios.patch(`${API_URL}/messages/${id}`, data, { headers: getHeaders() }),
  sendMessage: (id) =>
    axios.post(`${API_URL}/messages/${id}/send`, {}, { headers: getHeaders() }),
  deleteMessage: (id) =>
    axios.delete(`${API_URL}/messages/${id}`, { headers: getHeaders() }),
  getArchivedMembers: () =>
    axios.get(`${API_URL}/messages/archived-members`, { headers: getHeaders() }),

  // Noticeboard
  getNoticeboard: () =>
    axios.get(`${API_URL}/noticeboard`, { headers: getHeaders() }),
  createNoticeboardPost: (data) =>
    axios.post(`${API_URL}/noticeboard`, data, { headers: getHeaders() }),
  updateNoticeboardPost: (id, data) =>
    axios.patch(`${API_URL}/noticeboard/${id}`, data, { headers: getHeaders() }),
  deleteNoticeboardPost: (id) =>
    axios.delete(`${API_URL}/noticeboard/${id}`, { headers: getHeaders() }),
  markNoticeboardRead: (id) =>
    axios.post(`${API_URL}/noticeboard/${id}/read`, {}, { headers: getHeaders() }),

  // Recipient groups
  getRecipientGroups: () =>
    axios.get(`${API_URL}/recipient-groups`, { headers: getHeaders() }),
  createRecipientGroup: (data) =>
    axios.post(`${API_URL}/recipient-groups`, data, { headers: getHeaders() }),
  updateRecipientGroup: (id, data) =>
    axios.patch(`${API_URL}/recipient-groups/${id}`, data, { headers: getHeaders() }),
  deleteRecipientGroup: (id) =>
    axios.delete(`${API_URL}/recipient-groups/${id}`, { headers: getHeaders() }),

  // Noticeboard recipient preview
  previewNoticeboardRecipients: (recipientFilter) =>
    axios.post(`${API_URL}/noticeboard/preview-recipients`, { recipientFilter }, { headers: getHeaders() }),

  uploadNoticeboardImage: (formData) =>
    axios.post(`${API_URL}/noticeboard/upload-image`, formData, {
      headers: getHeaders(),
      // Do NOT set Content-Type — axios derives it from FormData including the boundary
    }),

  // Commitments
  getMyCommitments: () =>
    axios.get(`${API_URL}/commitments/mine-all`, { headers: getHeaders() }),
  getCommitmentsForTemplate: (templateId) =>
    axios.get(`${API_URL}/commitments?templateId=${templateId}`, { headers: getHeaders() }),
  getCommitmentsForGymnast: (gymnastId) =>
    axios.get(`${API_URL}/commitments/gymnast/${gymnastId}`, { headers: getHeaders() }),
  getMyCommitmentsForTemplate: (templateId) =>
    axios.get(`${API_URL}/commitments/mine?templateId=${templateId}`, { headers: getHeaders() }),
  createCommitment: (data) =>
    axios.post(`${API_URL}/commitments`, data, { headers: getHeaders() }),
  updateCommitmentStatus: (commitmentId, status) =>
    axios.patch(`${API_URL}/commitments/${commitmentId}/status`, { status }, { headers: getHeaders() }),
  deleteCommitment: (commitmentId) =>
    axios.delete(`${API_URL}/commitments/${commitmentId}`, { headers: getHeaders() }),

  // Charges
  getPayouts: ({ light } = {}) => axios.get(`${API_URL}/booking/payouts`, { headers: getHeaders(), ...(light && { params: { light: '1' } }) }),
  payoutsOnboardingLink: () => axios.post(`${API_URL}/booking/payouts/onboarding-link`, {}, { headers: getHeaders() }),
  payoutsAccountSession: () => axios.post(`${API_URL}/booking/payouts/account-session`, {}, { headers: getHeaders() }),
  billingSetup: (returnTo = 'card-payments') => axios.post(`${API_URL}/booking/payouts/billing-setup`, { returnTo }, { headers: getHeaders() }),
  refreshPayouts: () => axios.post(`${API_URL}/booking/payouts/refresh`, {}, { headers: getHeaders() }),
  getOAuthLink: () => axios.post(`${API_URL}/booking/payouts/oauth-link`, {}, { headers: getHeaders() }),
  completeOAuth: (code, state) => axios.post(`${API_URL}/booking/payouts/oauth-complete`, { code, state }, { headers: getHeaders() }),
  getMyCharges: () =>
    axios.get(`${API_URL}/booking/charges/my`, { headers: getHeaders() }),
  getAdminCharges: () =>
    axios.get(`${API_URL}/booking/charges`, { headers: getHeaders() }),
  getAdminPayments: (month) =>
    axios.get(`${API_URL}/booking/admin/payments${month ? `?month=${month}` : ''}`, { headers: getHeaders() }),
  getAdminPaymentsReceived: (month) =>
    axios.get(`${API_URL}/booking/admin/payments/received${month ? `?month=${month}` : ''}`, { headers: getHeaders() }),
  getChargesForUser: (userId) =>
    axios.get(`${API_URL}/booking/charges?userId=${encodeURIComponent(userId)}`, { headers: getHeaders() }),
  createCharge: (data) =>
    axios.post(`${API_URL}/booking/charges`, data, { headers: getHeaders() }),
  deleteCharge: (id) =>
    axios.delete(`${API_URL}/booking/charges/${id}`, { headers: getHeaders() }),
  deleteCredit: (id) =>
    axios.delete(`${API_URL}/booking/credits/${id}`, { headers: getHeaders() }),
  // Business invoicing (club admin)
  getBusinessContacts: () =>
    axios.get(`${API_URL}/booking/business-contacts`, { headers: getHeaders() }),
  getBusinessContact: (id) =>
    axios.get(`${API_URL}/booking/business-contacts/${id}`, { headers: getHeaders() }),
  createBusinessContact: (data) =>
    axios.post(`${API_URL}/booking/business-contacts`, data, { headers: getHeaders() }),
  updateBusinessContact: (id, data) =>
    axios.put(`${API_URL}/booking/business-contacts/${id}`, data, { headers: getHeaders() }),
  deleteBusinessContact: (id) =>
    axios.delete(`${API_URL}/booking/business-contacts/${id}`, { headers: getHeaders() }),
  getInvoices: (status) =>
    axios.get(`${API_URL}/booking/invoices${status ? `?status=${status}` : ''}`, { headers: getHeaders() }),
  getInvoice: (id) =>
    axios.get(`${API_URL}/booking/invoices/${id}`, { headers: getHeaders() }),
  createInvoice: (data) =>
    axios.post(`${API_URL}/booking/invoices`, data, { headers: getHeaders() }),
  updateInvoice: (id, data) =>
    axios.put(`${API_URL}/booking/invoices/${id}`, data, { headers: getHeaders() }),
  deleteInvoice: (id) =>
    axios.delete(`${API_URL}/booking/invoices/${id}`, { headers: getHeaders() }),
  sendInvoice: (id, { email = true } = {}) =>
    axios.post(`${API_URL}/booking/invoices/${id}/send`, { email }, { headers: getHeaders() }),
  markInvoicePaid: (id) =>
    axios.post(`${API_URL}/booking/invoices/${id}/mark-paid`, {}, { headers: getHeaders() }),
  emailInvoice: (id) =>
    axios.post(`${API_URL}/booking/invoices/${id}/email`, {}, { headers: getHeaders() }),
  applyCredit: (id, amount) =>
    axios.post(`${API_URL}/booking/invoices/${id}/apply-credit`, { amount }, { headers: getHeaders() }),
  getCreditNotes: () =>
    axios.get(`${API_URL}/booking/credit-notes`, { headers: getHeaders() }),
  createCreditNote: (data) =>
    axios.post(`${API_URL}/booking/credit-notes`, data, { headers: getHeaders() }),
  // PDFs need the auth header, so fetch as a blob and hand it to the browser
  downloadInvoicePdf: (id, filename) =>
    downloadPdf(`${API_URL}/booking/invoices/${id}/pdf`, filename),
  downloadCreditNotePdf: (id, filename) =>
    downloadPdf(`${API_URL}/booking/credit-notes/${id}/pdf`, filename),
  // type: 'coach' (one gymnast per page) or 'tariff' (one sheet per category entered)
  downloadCompetitorSheets: (eventId, type, filename) =>
    downloadPdf(`${API_URL}/booking/competition-events/${eventId}/sheets?type=${type}`, filename),

  // Attendance register
  getAttendance: (instanceId) =>
    axios.get(`${API_URL}/booking/attendance/${instanceId}`, { headers: getHeaders() }),

  createAttendance: (instanceId, data) =>
    axios.post(`${API_URL}/booking/attendance/${instanceId}`, data, { headers: getHeaders() }),

  deleteAttendance: (instanceId, gymnastId) =>
    axios.delete(`${API_URL}/booking/attendance/${instanceId}/${gymnastId}`, { headers: getHeaders() }),

  // Incidents
  getIncidents: () =>
    axios.get(`${API_URL}/incidents`, { headers: getHeaders() }),
  getMyIncidents: () =>
    axios.get(`${API_URL}/incidents/mine`, { headers: getHeaders() }),
  getIncident: (id) =>
    axios.get(`${API_URL}/incidents/${id}`, { headers: getHeaders() }),
  createIncident: (data) =>
    axios.post(`${API_URL}/incidents`, data, { headers: getHeaders() }),
  updateIncident: (id, data) =>
    axios.patch(`${API_URL}/incidents/${id}`, data, { headers: getHeaders() }),
  deleteIncident: (id) =>
    axios.delete(`${API_URL}/incidents/${id}`, { headers: getHeaders() }),
  forwardIncident: (id, data) =>
    axios.post(`${API_URL}/incidents/${id}/forward`, data, { headers: getHeaders() }),

  // Preview as (developers only)
  getPreviewRoles: () =>
    axios.get(`${API_URL}/preview`, { headers: getHeaders() }),
  startPreview: (role) =>
    axios.post(`${API_URL}/preview/${role}`, {}, { headers: getHeaders() }),
  // A club admin previewing one of the club's roles (Roles page)
  startRolePreview: (roleId) =>
    axios.post(`${API_URL}/preview/role/${roleId}`, {}, { headers: getHeaders() }),

  // Welfare reports
  getWelfareReports: () =>
    axios.get(`${API_URL}/welfare`, { headers: getHeaders() }),
  getWelfarePeople: () =>
    axios.get(`${API_URL}/welfare/people`, { headers: getHeaders() }),
  getWelfareReport: (id) =>
    axios.get(`${API_URL}/welfare/${id}`, { headers: getHeaders() }),
  createWelfareReport: (data) =>
    axios.post(`${API_URL}/welfare`, data, { headers: getHeaders() }),
  updateWelfareReport: (id, data) =>
    axios.patch(`${API_URL}/welfare/${id}`, data, { headers: getHeaders() }),
  deleteWelfareReport: (id) =>
    axios.delete(`${API_URL}/welfare/${id}`, { headers: getHeaders() }),
  uploadWelfareAttachments: (id, files) => {
    const formData = new FormData();
    for (const f of files) formData.append('files', f);
    return axios.post(`${API_URL}/welfare/${id}/attachments`, formData, {
      headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
    });
  },
  fetchWelfareAttachment: (welfareId, attachmentId) =>
    axios.get(`${API_URL}/welfare/${welfareId}/attachments/${attachmentId}/file`, {
      headers: getHeaders(),
      responseType: 'blob',
    }),
  signWelfareReport: (id, kind, fullName) =>
    axios.post(`${API_URL}/welfare/${id}/sign`, { kind, fullName }, { headers: getHeaders() }),
  getWelfareShareRecipients: (id) =>
    axios.get(`${API_URL}/welfare/${id}/share-recipients`, { headers: getHeaders() }),
  shareWelfareOutcome: (id, recipientUserId, message, includeReport = false) =>
    axios.post(`${API_URL}/welfare/${id}/shares`, { recipientUserId, message, includeReport }, { headers: getHeaders() }),
  withdrawWelfareShare: (id, shareId) =>
    axios.delete(`${API_URL}/welfare/${id}/shares/${shareId}`, { headers: getHeaders() }),
  deleteWelfareAttachment: (welfareId, attachmentId) =>
    axios.delete(`${API_URL}/welfare/${welfareId}/attachments/${attachmentId}`, { headers: getHeaders() }),

  // Recurring credits
  getRecurringCredits: () =>
    axios.get(`${API_URL}/booking/recurring-credits`, { headers: getHeaders() }),

  createRecurringCredit: (data) =>
    axios.post(`${API_URL}/booking/recurring-credits`, data, { headers: getHeaders() }),

  deleteRecurringCredit: (id) =>
    axios.delete(`${API_URL}/booking/recurring-credits/${id}`, { headers: getHeaders() }),

  // Competition Events (admin)
  getCompetitionEvents: () =>
    axios.get(`${API_URL}/booking/competition-events`, { headers: getHeaders() }),

  getCompetitionEvent: (id) =>
    axios.get(`${API_URL}/booking/competition-events/${id}`, { headers: getHeaders() }),

  categoriesForGymnast: (gymnastId, apparatus) =>
    axios.get(`${API_URL}/booking/competition-categories/for-gymnast/${gymnastId}`, { params: { apparatus }, headers: getHeaders() }),

  checkAgainstCategory: (categoryId, body) =>
    axios.post(`${API_URL}/booking/competition-categories/${categoryId}/check`, body, { headers: getHeaders() }),

  createCompetitionEvent: (data) =>
    axios.post(`${API_URL}/booking/competition-events`, data, { headers: getHeaders() }),

  updateCompetitionEvent: (id, data) =>
    axios.patch(`${API_URL}/booking/competition-events/${id}`, data, { headers: getHeaders() }),

  deleteCompetitionEvent: (id) =>
    axios.delete(`${API_URL}/booking/competition-events/${id}`, { headers: getHeaders() }),

  getEligibleGymnasts: (eventId) =>
    axios.get(`${API_URL}/booking/competition-events/${eventId}/eligible`, { headers: getHeaders() }),

  getAllCompetitionGymnasts: (eventId) =>
    axios.get(`${API_URL}/booking/competition-events/${eventId}/all-gymnasts`, { headers: getHeaders() }),

  addCompetitionCategory: (eventId, data) =>
    axios.post(`${API_URL}/booking/competition-events/${eventId}/categories`, data, { headers: getHeaders() }),

  updateCompetitionCategory: (eventId, catId, data) =>
    axios.patch(`${API_URL}/booking/competition-events/${eventId}/categories/${catId}`, data, { headers: getHeaders() }),

  deleteCompetitionCategory: (eventId, catId) =>
    axios.delete(`${API_URL}/booking/competition-events/${eventId}/categories/${catId}`, { headers: getHeaders() }),

  inviteGymnasts: (eventId, gymnastIds, categoryIds = [], priceOverride = null, entryType = 'INDIVIDUAL') =>
    axios.post(`${API_URL}/booking/competition-events/${eventId}/invite`, { gymnastIds, categoryIds, priceOverride, entryType }, { headers: getHeaders() }),

  // Competition Entries
  getMyCompetitionEntries: () =>
    axios.get(`${API_URL}/booking/competition-entries/mine`, { headers: getHeaders() }),

  getCompetitionEntrySummary: () =>
    axios.get(`${API_URL}/booking/competition-entries/admin-summary`, { headers: getHeaders() }),

  updateCompetitionEntry: (entryId, data) =>
    axios.patch(`${API_URL}/booking/competition-entries/${entryId}`, data, { headers: getHeaders() }),

  getEntryFamilyCredit: (entryId) =>
    axios.get(`${API_URL}/booking/competition-entries/${entryId}/family-credit`, { headers: getHeaders() }),

  setEntryRoutine: (entryId, data) =>
    axios.put(`${API_URL}/booking/competition-entries/${entryId}/routines`, data, { headers: getHeaders() }),

  applyEntrySetRoutine: (entryId, data) =>
    axios.post(`${API_URL}/booking/competition-entries/${entryId}/routines/set`, data, { headers: getHeaders() }),

  checkEntry: (entryId) =>
    axios.get(`${API_URL}/booking/competition-entries/${entryId}/check`, { headers: getHeaders() }),

  copyCompetitionCategories: (eventId, fromEventId) =>
    axios.post(`${API_URL}/booking/competition-events/${eventId}/copy-categories`, { fromEventId }, { headers: getHeaders() }),

  getGenderNotSet: (eventId) =>
    axios.get(`${API_URL}/booking/competition-events/${eventId}/gender-not-set`, { headers: getHeaders() }),

  deleteCompetitionEntry: (entryId) =>
    axios.delete(`${API_URL}/booking/competition-entries/${entryId}`, { headers: getHeaders() }),

  acceptCompetitionEntry: (entryId) =>
    axios.post(`${API_URL}/booking/competition-entries/${entryId}/accept`, {}, { headers: getHeaders() }),

  declineCompetitionEntry: (entryId) =>
    axios.post(`${API_URL}/booking/competition-entries/${entryId}/decline`, {}, { headers: getHeaders() }),

  sendCompetitionReminder: (entryId) =>
    axios.post(`${API_URL}/booking/competition-entries/${entryId}/send-reminder`, {}, { headers: getHeaders() }),

  // Take a paid gymnast out, giving `credit` pence back as account credit
  withdrawCompetitionEntry: (entryId, credit, reason) =>
    axios.post(`${API_URL}/booking/competition-entries/${entryId}/withdraw`, { credit, reason }, { headers: getHeaders() }),

  waiveCompetitionEntry: (entryId, reason) =>
    axios.post(`${API_URL}/booking/competition-entries/${entryId}/waive`, { reason }, { headers: getHeaders() }),

  markCompetitionEntryPaid: (entryId, amount, note) =>
    axios.post(`${API_URL}/booking/competition-entries/${entryId}/mark-paid`, { amount, note }, { headers: getHeaders() }),

  checkoutCompetitionEntry: (entryId) =>
    axios.post(`${API_URL}/booking/competition-entries/${entryId}/checkout`, {}, { headers: getHeaders() }),

  reinviteCompetitionEntry: (entryId) =>
    axios.post(`${API_URL}/booking/competition-entries/${entryId}/reinvite`, {}, { headers: getHeaders() }),

  // Session coaches
  getMySessions: (days = 14) =>
    axios.get(`${API_URL}/booking/session-coaches/mine`, { params: { days }, headers: getHeaders() }),

  getCoachHours: (from, to) =>
    axios.get(`${API_URL}/booking/session-coaches/hours`, { params: { from, to }, headers: getHeaders() }),

  getSessionTypes: () =>
    axios.get(`${API_URL}/booking/session-types`, { headers: getHeaders() }),

  getApprovalsWaiting: () =>
    axios.get(`${API_URL}/booking/session-types/waiting`, { headers: getHeaders() }),

  getMyApprovalWait: () =>
    axios.get(`${API_URL}/booking/session-types/waiting-for-me`, { headers: getHeaders() }),

  createSessionType: (data) =>
    axios.post(`${API_URL}/booking/session-types`, data, { headers: getHeaders() }),

  updateSessionType: (id, data) =>
    axios.put(`${API_URL}/booking/session-types/${id}`, data, { headers: getHeaders() }),

  getCoachStaff: () =>
    axios.get(`${API_URL}/booking/session-coaches/staff`, { headers: getHeaders() }),

  setDateCoaches: (instanceId, coachIds) =>
    axios.put(`${API_URL}/booking/session-coaches/instances/${instanceId}`, { coachIds }, { headers: getHeaders() }),

  clearDateCoaches: (instanceId) =>
    axios.delete(`${API_URL}/booking/session-coaches/instances/${instanceId}`, { headers: getHeaders() }),
};

// Audit log
export const getAuditLog = (params) =>
  axios.get(`${API_URL}/booking/admin/audit-log`, { params, headers: getHeaders() });

export const getAuditStaff = () =>
  axios.get(`${API_URL}/booking/admin/audit-log/staff`, { headers: getHeaders() });

export const getAuditActions = () =>
  axios.get(`${API_URL}/booking/admin/audit-log/actions`, { headers: getHeaders() });

// Session templates (admin)
export const getTemplates = () =>
  axios.get(`${API_URL}/booking/templates`, { headers: getHeaders() });

export const createTemplate = (data) =>
  axios.post(`${API_URL}/booking/templates`, data, { headers: getHeaders() });

export const updateTemplate = (id, data) =>
  axios.put(`${API_URL}/booking/templates/${id}`, data, { headers: getHeaders() });

export const toggleTemplate = (id, applyToFutureInstances) =>
  axios.patch(`${API_URL}/booking/templates/${id}/toggle`, { applyToFutureInstances }, { headers: getHeaders() });

export const deleteTemplate = (id, applyToFutureInstances) =>
  axios.delete(`${API_URL}/booking/templates/${id}`, { data: { applyToFutureInstances }, headers: getHeaders() });

// Guardian invites
export const getGymnastGuardians = (gymnastId) =>
  axios.get(`${API_URL}/guardian-invites/gymnast/${gymnastId}`, { headers: getHeaders() });

export const getInviteByToken = (token) =>
  axios.get(`${API_URL}/guardian-invites/token/${token}`);

export const createGuardianInvite = (data) =>
  axios.post(`${API_URL}/guardian-invites`, data, { headers: getHeaders() });

export const acceptGuardianInvite = (token) =>
  axios.post(`${API_URL}/guardian-invites/token/${token}/accept`, {}, { headers: getHeaders() });

export const cancelGuardianInvite = (id) =>
  axios.delete(`${API_URL}/guardian-invites/${id}`, { headers: getHeaders() });

export const removeGuardian = (gymnastId, userId) =>
  axios.delete(`${API_URL}/guardian-invites/gymnast/${gymnastId}/guardian/${userId}`, { headers: getHeaders() });

export const linkGuardianDirect = (gymnastId, userId) =>
  axios.post(`${API_URL}/guardian-invites/gymnast/${gymnastId}/guardian/link`, { userId }, { headers: getHeaders() });

// Named contacts
export const createNamedContact = (data) =>
  axios.post(`${API_URL}/named-contacts`, data, { headers: getHeaders() });

export const updateNamedContact = (id, data) =>
  axios.patch(`${API_URL}/named-contacts/${id}`, data, { headers: getHeaders() });

export const deleteNamedContact = (id) =>
  axios.delete(`${API_URL}/named-contacts/${id}`, { headers: getHeaders() });
